import { useI18N } from "common/hooks";
import { WarningIcon, ImagesIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { VenueDetails } from "common/types";

type MainTab = "accessibility" | "alerts" | "photos" | "reviews";

type VenueStatsRowProps = {
  alertCount: number;
  photoCount: number;
  venueDetails?: VenueDetails;
  /** Number of experiences actually loaded — drives the sweet/sour treatment. */
  reviewCount: number;
  onSelectTab: (tab: MainTab) => void;
};

/**
 * The three at-a-glance counts under the venue title: access alerts, venue
 * photos and experiences. Each cell switches the main tab. Desktop only —
 * mobile keeps its own summary line.
 */
export default function VenueStatsRow({
  alertCount,
  photoCount,
  venueDetails,
  reviewCount,
  onSelectTab,
}: VenueStatsRowProps) {
  const i18n = useI18N();

  const hasAlerts = alertCount > 0;

  // Experiences reuse the existing sweetness split: below three ratings the
  // sample is too small to call, so the venue is still "tasting".
  const coolPercentage =
    venueDetails?.venueAccessibilityExperienceStats?.coolPercentage ?? 0;
  const weirdPercentage =
    venueDetails?.venueAccessibilityExperienceStats?.weirdPercentage ?? 0;
  const isSweet = coolPercentage >= weirdPercentage;
  const experienceTotal =
    venueDetails?.venueAccessibilityExperienceStats?.totalCount ?? 0;

  const hasVerdict = reviewCount >= 3;
  const isTasting = reviewCount > 0 && reviewCount < 3;

  const StatDivider = () => (
    <div
      className="w-px self-stretch my-1.5 bg-border-token-neutral-minimal shrink-0"
      aria-hidden="true"
    />
  );

  const cellBase =
    // 6px / 21px value / 2px / 16px label / 6px = Figma's 51px cell.
    "flex-1 flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg transition-colors focus-blue";

  return (
    <div className="flex items-stretch" role="group">
      {/* Access alerts */}
      <button
        type="button"
        onClick={() => onSelectTab("alerts")}
        className={cn(
          cellBase,
          hasAlerts
            ? "border border-border-token-error-minimal bg-bg-error-tertiary-default hover:bg-bg-error-tertiary-hover"
            : "hover:bg-bg-brand-ghost-hover",
        )}
        aria-label={`${alertCount} ${i18n.get("accessAlerts")}`}
      >
        <span className="flex items-center gap-1.5">
          <WarningIcon
            size={20}
            weight={hasAlerts ? "fill" : "regular"}
            aria-hidden="true"
            className={
              hasAlerts ? "text-fg-error-subtle" : "text-fg-neutral-strong"
            }
          />
          <span
            className={cn(
              "title-s-semibold",
              hasAlerts ? "text-fg-error-subtle" : "text-fg-neutral-strong",
            )}
          >
            {alertCount}
          </span>
        </span>
        <span
          className={cn(
            "label-s-medium whitespace-nowrap",
            hasAlerts ? "text-fg-error-subtle" : "text-fg-neutral-subtle",
          )}
        >
          {i18n.get("accessAlerts")}
        </span>
      </button>

      {/* The alert cell already draws its own border when there are alerts,
          so a divider right after it would double up as a visible line. */}
      {!hasAlerts && <StatDivider />}

      {/* Venue photos */}
      <button
        type="button"
        onClick={() => onSelectTab("photos")}
        className={cn(cellBase, "hover:bg-bg-brand-ghost-hover")}
        aria-label={`${photoCount} ${i18n.get("venuePhotos")}`}
      >
        <span className="flex items-center gap-1.5">
          <ImagesIcon
            size={20}
            aria-hidden="true"
            className="text-fg-neutral-strong"
          />
          <span className="title-s-semibold text-fg-neutral-strong">
            {photoCount}
          </span>
        </span>
        <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
          {i18n.get("venuePhotos")}
        </span>
      </button>

      <StatDivider />

      {/* Experiences */}
      <button
        type="button"
        onClick={() => onSelectTab("reviews")}
        className={cn(cellBase, "hover:bg-bg-brand-ghost-hover")}
        aria-label={`${experienceTotal} ${i18n.get("experiences")}`}
      >
        <span className="flex items-center gap-1.5">
          {/* No experiences yet reads as a leaf — nothing has ripened into a
              sweet/sour verdict, so the fruit icons would be misleading. */}
          <img
            src={
              !hasVerdict && !isTasting
                ? "/images/Leaf.svg"
                : isTasting
                  ? "/images/Plant.svg"
                  : isSweet
                    ? "/images/Orange.svg"
                    : "/images/OrangeSlice.svg"
            }
            alt=""
            aria-hidden="true"
            className="w-5 h-5"
          />
          <span className="title-s-semibold text-fg-neutral-strong whitespace-nowrap">
            {hasVerdict
              ? i18n.get(isSweet ? "sweet" : "sour")
              : isTasting
                ? i18n.get("tasting")
                : "--"}
          </span>
        </span>
        <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
          {experienceTotal}{" "}
          {(experienceTotal === 1
            ? i18n.get("experience")
            : i18n.get("experiences")
          ).toLowerCase()}
        </span>
      </button>
    </div>
  );
}
